import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './ServiceForm.module.css';

// Reusable Components
import { CustomDropdown } from '../ServiceProvider/redesigncatering/Customdropdown';
import { ItemInputWithDropdown } from '../ServiceProvider/redesigncatering/ItemInputWithDropdown';
import { CategoryDropdown } from '../ServiceProvider/redesigncatering/CategoryDropdown';
import MenuDialog from '../MenuDialog';

const ServiceForm = React.memo(({
  service = null,
  onSubmit,
  onCancel,
  categories = [],
  itemsCategories = [],
  includedItemsList = [],
  allMasterItems,
  sidessallMasterItems,
  isEditing = false,
  onToggleEdit,
  selectedcategory,
  deleteButton = null,
  mode = "view",
  onPackageSelect,
  onBack
}) => {
  const isEditMode = !!service;
  const isCreateMode = mode === "create";
  const isViewMode = mode === "view";
  const isEditFormMode = mode === "edit";

  const [isFormEditable, setIsFormEditable] = useState(isEditing || isCreateMode || isEditFormMode);

  // State for selected items organized by tab and subTab
  const [selectedIncludedItems, setSelectedIncludedItems] = useState([]);
  const [selectedMealTypeId, setSelectedMealTypeId] = useState(selectedcategory);

  // State for tracking which tab/subtab is being edited
  const [activeTabId, setActiveTabId] = useState(null);
  const [activeSubTabId, setActiveSubTabId] = useState(null);
  const [activeTabName, setActiveTabName] = useState('');
  const [activeSubTabName, setActiveSubTabName] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);

  // Menu Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // Item selection modal states
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const [allAvailableItems, setAllAvailableItems] = useState([]);

  // State for validation
  const [formValid, setFormValid] = useState(true);

  const [formData, setFormData] = useState({
    item_id: service?.item_id || null,
    custom_combo_name: service?.custom_combo_name || '',
    meal_id: selectedcategory || service?.meal_id || '',
    included_items: service?.included_items || [],
  });

  const itemswithparentlist = allMasterItems || [];

  const [isAddingItems, setIsAddingItems] = useState(false);

  const initialFormDataRef = useRef(null);
  const changedFieldsRef = useRef(new Set());

  // Process itemsCategories data structure
  const tabs = itemsCategories || [];

  // Get main items for display (catering_main equivalent)
  const catering_main = selectedIncludedItems.map(item => ({
    itemName: item.itemName,
    qty: item.qty || 1,
    itemGroupName: item.itemGroupName || null,
    dietCategoryName: item.dietCategoryName || null
  }));

  // Handle See More click
  const handleSeeMore = useCallback((menuType, items) => {
    console.log("Opening dialog with items:", items?.length);
    setSelectedMenu({
      label: menuType,
      items: items
    });
    setIsDialogOpen(true);
  }, []);

  // Handle Close Dialog
  const handleCloseDialog = useCallback(() => {
    console.log("Closing dialog");
    setIsDialogOpen(false);
    setSelectedMenu(null);
  }, []);

  // Prepare all available items for the modal
  useEffect(() => {
    if (allMasterItems && allMasterItems.length > 0) {
      const items = [];
      allMasterItems.forEach(categoryData => {
        if (categoryData.items && categoryData.items.length > 0) {
          categoryData.items.forEach(item => {
            items.push({
              ...item,
              categoryName: categoryData.category?.name || 'Uncategorized',
              categoryId: categoryData.category?.id,
              itemGroup: item.itemGroup || null,
              itemGroupId: item.itemGroup?.id || null,
              itemGroupName: item.itemGroup?.group_name || null,
              itemSubGroupId: item.itemSubGroupId || 0,
              itemSubGroupName: item.itemSubGroupName || ""
            });
          });
        }
      });
      setAllAvailableItems(items);
    }
  }, [allMasterItems]);

  useEffect(() => {
    setIsFormEditable(isEditing || isCreateMode || isEditFormMode);
  }, [isEditing, isCreateMode, isEditFormMode]);

  const findCategoryForItem = useCallback((item) => {
    if (!item || (!item.itemId && !item.itemName)) return null;

    for (const categoryData of itemswithparentlist) {
      if (categoryData.items && categoryData.items.length > 0) {
        const foundItem = categoryData.items.find(catItem =>
          catItem.id === item.itemId ||
          catItem.itemName === item.itemName ||
          catItem.name === item.itemName
        );
        if (foundItem) {
          return categoryData.category;
        }
      }
    }
    return null;
  }, [itemswithparentlist]);

  const findItemDetails = useCallback((item) => {
    if (!item || (!item.itemId && !item.itemName)) return null;

    for (const categoryData of itemswithparentlist) {
      if (categoryData.items && categoryData.items.length > 0) {
        const foundItem = categoryData.items.find(catItem =>
          catItem.id === item.itemId ||
          catItem.itemName === item.itemName ||
          catItem.name === item.itemName
        );
        if (foundItem) {
          return foundItem;
        }
      }
    }
    return null;
  }, [itemswithparentlist]);

  // Validate form - check if at least one item is selected
  const validateForm = useCallback(() => {
    const hasItems = selectedIncludedItems.length > 0;
    const hasPackageName = formData.custom_combo_name && formData.custom_combo_name.trim().length > 0;
    setFormValid(hasItems && hasPackageName);
  }, [selectedIncludedItems, formData.custom_combo_name]);

  // Filter items based on selected tab, subtab, and search query
  const getFilteredItems = useCallback(() => {
    let filtered = allAvailableItems;

    const selectedGroupId = (activeSubTabId && activeSubTabId !== 0) ? activeSubTabId : activeTabId;

    if (selectedGroupId && selectedGroupId !== 0) {
      filtered = filtered.filter(item => {
        const itemGroupId = item.itemGroupId || item.itemGroup?.id || 0;
        return itemGroupId === selectedGroupId;
      });
    }

    if (searchQuery && searchQuery.trim()) {
      filtered = filtered.filter(item =>
        (item.itemName || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allAvailableItems, activeTabId, activeSubTabId, searchQuery]);

  // Toggle item selection in modal
  const toggleItemSelection = useCallback((item) => {
    setTempSelectedItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, {
          ...item,
          itemId: item.id,
          itemName: item.itemName,
          qty: 1,
          itemGroupId: activeTabId,
          itemSubGroupId: activeSubTabId,
          isCounter: true,
          dietCategoryName: item.dietCategoryName || null,
          itemGroup: item.itemGroup,
          itemGroupName: activeTabName
        }];
      }
    });
  }, [activeTabId, activeSubTabId, activeTabName]);

  // Update quantity of temp selected item
  const updateTempItemQuantity = useCallback((item, newQty) => {
    setTempSelectedItems(prev =>
      prev.map(i =>
        i.id === item.id ? { ...i, qty: Math.max(1, newQty) } : i
      )
    );
  }, []);

  // Confirm and add selected items
  const confirmItemSelection = useCallback(() => {
    setSelectedIncludedItems(prev => {
      const filtered = prev.filter(item =>
        !(item.itemGroupId === activeTabId && item.itemSubGroupId === activeSubTabId)
      );
      return [...filtered, ...tempSelectedItems];
    });
    setShowItemModal(false);
    setSearchQuery('');
  }, [activeTabId, activeSubTabId, tempSelectedItems]);

  // Open item selection modal for a specific tab/subtab
  const openItemSelection = useCallback((tabId, subTabId = 0, tabName = '', subTabName = '') => {
    setActiveTabId(tabId);
    setActiveSubTabId(subTabId);
    setActiveTabName(tabName);
    setActiveSubTabName(subTabName);

    const existingItems = selectedIncludedItems.filter(
      item => item.itemGroupId === tabId && item.itemSubGroupId === subTabId
    );
    setTempSelectedItems([...existingItems]);
    setSearchQuery('');
    setShowItemModal(true);
  }, [selectedIncludedItems]);

  // Stepper button component for quantity adjustment
  const StepperButton = ({ label, onClick, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={styles.stepperButton}
    >
      {label}
    </button>
  );

  // Update item quantity
  const updateItemQuantity = useCallback((item, newQty) => {
    setSelectedIncludedItems(prev =>
      prev.map(i =>
        i === item ? { ...i, qty: Math.max(1, newQty) } : i
      )
    );
  }, []);

  // Remove item from selected list
  const removeItem = useCallback((itemToRemove) => {
    setSelectedIncludedItems(prev => prev.filter(item => item !== itemToRemove));
  }, []);

  // Build included items section UI based on tab structure
  const buildIncludedItemsSection = (tab) => {
    const hasSubGroups = tab.subGroups && tab.subGroups.length > 0;

    if (hasSubGroups) {
      return (
        <div key={tab.id} className={styles.tabSection}>
          <div className={styles.tabHeader}>
            <h5 className={styles.tabTitle}>{tab.group_name}</h5>
          </div>
          <div className={styles.subGroupsContainer}>
            {tab.subGroups.map((subGroup) => {
              const itemsForSubTab = selectedIncludedItems.filter(
                item => item.itemGroupId === tab.id && item.itemSubGroupId === subGroup.id
              );

              return (
                <div key={subGroup.id} className={styles.subGroupCard}>
                  <div className={styles.subGroupHeader}>
                    <span className={styles.subGroupTitle}>{subGroup.group_name}</span>
                    <button
                      type="button"
                      onClick={() => openItemSelection(tab.id, subGroup.id, tab.group_name, subGroup.group_name)}
                      className={styles.addButton}
                    >
                      ADD
                    </button>
                  </div>

                  {itemsForSubTab.length > 0 && <div className={styles.divider} />}

                  {itemsForSubTab.map((item, idx) => (
                    <div key={idx} className={styles.selectedItemRow}>
                      <div className={styles.itemInfo}>
                        {item.dietCategoryName && (
                          <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                        )}
                        <span className={styles.itemName}>{item.itemName || '-NA-'}</span>
                      </div>

                      <div className={styles.stepperContainer}>
                        <StepperButton
                          label="-"
                          onClick={() => {
                            if (item.qty > 1) {
                              updateItemQuantity(item, item.qty - 1);
                            }
                          }}
                          disabled={item.qty <= 1}
                        />
                        <span className={styles.quantity}>{item.qty}</span>
                        <StepperButton
                          label="+"
                          onClick={() => updateItemQuantity(item, item.qty + 1)}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item)}
                        className={styles.deleteButton}
                      >
                        <i className="pi pi-trash" style={{ fontSize: '16px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      const itemsForTab = selectedIncludedItems.filter(
        item => item.itemGroupId === tab.id && item.itemSubGroupId === 0
      );

      return (
        <div key={tab.id} className={styles.tabSection}>
          <div className={styles.tabHeader}>
            <span className={styles.tabTitle}>{tab.group_name}</span>
            <button
              type="button"
              onClick={() => openItemSelection(tab.id, 0, tab.group_name, '')}
              className={styles.addButton}
            >
              ADD
            </button>
          </div>

          {itemsForTab.length > 0 && (
            <div className={styles.itemsContainer}>
              {itemsForTab.map((item, idx) => (
                <div key={idx} className={styles.selectedItemRow}>
                  <div className={styles.itemInfo}>
                    {item.dietCategoryName && (
                      <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                    )}
                    <span className={styles.itemName}>{item.itemName || '-NA-'}</span>
                  </div>

                  <div className={styles.stepperContainer}>
                    <StepperButton
                      label="-"
                      onClick={() => {
                        if (item.qty > 1) {
                          updateItemQuantity(item, item.qty - 1);
                        }
                      }}
                      disabled={item.qty <= 1}
                    />
                    <span className={styles.quantity}>{item.qty}</span>
                    <StepperButton
                      label="+"
                      onClick={() => updateItemQuantity(item, item.qty + 1)}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItem(item)}
                    className={styles.deleteButton}
                  >
                    <i className="pi pi-trash" style={{ fontSize: '16px' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
  };

  useEffect(() => {
    if (service) {
      const processedIncludedItems = service.included_items?.length > 0
        ? service.included_items.map(item => {
          const itemCategory = findCategoryForItem(item);
          const itemDetails = findItemDetails(item);

          return {
            ...item,
            category: itemCategory,
            itemId: item.itemId || itemDetails?.id || null,
            itemName: item.itemName || itemDetails?.itemName || itemDetails?.name || '',
            user_package_id: item.user_package_id || null,
            itemGroupId: item.itemGroupId || null,
            itemSubGroupId: item.itemSubGroupId || 0,
            isCounter: true,
            dietCategoryName: item.dietCategoryName || null,
            qty: item.qty || 1
          };
        })
        : [];

      setSelectedIncludedItems(processedIncludedItems);

      const initialData = {
        item_id: service.item_id,
        custom_combo_name: service.custom_combo_name || '',
        meal_id: selectedcategory || service.meal_id || '',
        included_items: processedIncludedItems,
        package_id: service.package_id || null,
      };

      setFormData(initialData);

      if (isEditMode) {
        initialFormDataRef.current = JSON.parse(JSON.stringify(initialData));
        changedFieldsRef.current.clear();
      }
    } else {
      const createData = {
        item_id: null,
        custom_combo_name: '',
        meal_id: selectedcategory || '',
        included_items: [],
      };

      setFormData(createData);
      initialFormDataRef.current = null;
      changedFieldsRef.current.clear();
    }
  }, [service, isEditMode, selectedcategory, findCategoryForItem, findItemDetails]);

  // Auto-validate when formData or selectedIncludedItems changes
  useEffect(() => {
    validateForm();
  }, [formData.custom_combo_name, selectedIncludedItems, validateForm]);

  const trackFieldChange = useCallback((field) => {
    if (isEditMode) {
      changedFieldsRef.current.add(field);
    }
  }, [isEditMode]);

  const handleInputChange = useCallback((field, value) => {
    console.log("handleInputChange called with:", field, value);
    setFormData(prev => {
      console.log("Previous formData:", prev);
      const newFormData = {
        ...prev,
        [field]: value
      };
      console.log("New formData:", newFormData);
      return newFormData;
    });
    trackFieldChange(field);
  }, [trackFieldChange]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (selectedIncludedItems.length === 0) {
      alert('Please add at least one item before submitting.');
      return;
    }

    if (!formData.custom_combo_name || !formData.custom_combo_name.trim()) {
      alert('Please enter a package name.');
      return;
    }

    const submitData = {
      custom_combo_name: formData.custom_combo_name.trim(),
      package_id: formData.package_id || null,
      included_items: selectedIncludedItems.map(item => ({
        itemName: item.itemName,
        qty: parseInt(item.qty) || 1,
        itemId: item.itemId || null,
        unitType: null,
        user_package_id: item.user_package_id || null,
        itemGroupId: item.itemGroupId,
        itemSubGroupId: item.itemSubGroupId
      })),
      meal_id: formData.meal_id || selectedcategory,
    };

    if (isEditMode) {
      submitData.item_id = formData.item_id;
    }

    onSubmit(submitData, isEditMode);

    if (isEditMode) {
      initialFormDataRef.current = JSON.parse(JSON.stringify(formData));
      changedFieldsRef.current.clear();
    }
  }, [formData, selectedIncludedItems, isEditMode, selectedcategory, onSubmit]);

  const handlePackageSelection = useCallback(() => {
    if (onPackageSelect && service) {
      const packageData = {
        packageName: service.custom_combo_name,
        includedItems: service.included_items || [],
        itemId: service.item_id,
        mealType: service.meal?.meal_name,
        category: service.type?.name,
        providerId: service.provider_id
      };
      onPackageSelect(packageData);
    }
  }, [onPackageSelect, service]);

  // Item Selection Modal Component
  const ItemSelectionModal = () => {
    if (!showItemModal) return null;

    const filteredItems = getFilteredItems();

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>Select Items - {activeSubTabName || activeTabName}</h3>
            <button onClick={() => setShowItemModal(false)} className={styles.closeButton}>
              <i className="pi pi-times" />
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.searchContainer}>
              <i className="pi pi-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                style={{ paddingLeft: '35px' }}
              />
            </div>

            <div className={styles.itemsListContainer}>
              {filteredItems.length === 0 ? (
                <div className={styles.noItemsFound}>
                  <p>No items found matching your criteria</p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = tempSelectedItems.some(i => i.id === item.id);

                  return (
                    <div key={item.id} className={`${styles.modalItemRow} ${isSelected ? styles.selected : ''}`}>
                      <div className={styles.modalItemInfo}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItemSelection(item)}
                          className={styles.checkbox}
                        />

                        {item.dietCategoryName && (
                          <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                        )}

                        <div className={styles.modalItemDetails}>
                          <span className={styles.modalItemName}>{item.itemName}</span>
                          {item.itemGroupName && (
                            <span className={styles.modalItemGroup}>{item.itemGroupName}</span>
                          )}
                        </div>
                      </div>

                      {isSelected && (
                        <div className={styles.modalStepperContainer}>
                          <button
                            type="button"
                            onClick={() => {
                              const selectedItem = tempSelectedItems.find(i => i.id === item.id);
                              if (selectedItem && selectedItem.qty > 1) {
                                updateTempItemQuantity(selectedItem, selectedItem.qty - 1);
                              } else if (selectedItem && selectedItem.qty === 1) {
                                toggleItemSelection(item);
                              }
                            }}
                            className={styles.stepperButton}
                          >
                            -
                          </button>
                          <span className={styles.modalQuantity}>
                            {tempSelectedItems.find(i => i.id === item.id)?.qty || 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const selectedItem = tempSelectedItems.find(i => i.id === item.id);
                              if (selectedItem) {
                                updateTempItemQuantity(selectedItem, selectedItem.qty + 1);
                              } else {
                                toggleItemSelection(item);
                              }
                            }}
                            className={styles.stepperButton}
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className={styles.modalFooter}>
            <div className={styles.selectionSummary}>
              <span>{tempSelectedItems.length} item(s) selected</span>
            </div>
            <div className={styles.modalActions}>
              <button onClick={() => setShowItemModal(false)} className={styles.cancelButton}>
                Cancel
              </button>
              <button onClick={confirmItemSelection} className={styles.submitButton}>
                Add Selected ({tempSelectedItems.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={isViewMode ? styles.viewServiceCard : styles.editServiceCard}>
      <div className={styles.formHeader}>
        {isEditMode && (
          <div className={styles.headerLeft}>
            <span className={styles.cardTitle}>
              {service?.custom_combo_name || "Menu Item"}
            </span>
          </div>
        )}

        {isEditMode && isViewMode && (
          <div className={styles.headerRight}>
            <button
              type="button"
              onClick={onToggleEdit}
              className={styles.editButton}
            >
              <i className="pi pi-pencil" style={{ marginRight: '8px' }}></i>
              Edit
            </button>
            {deleteButton && (
              <div className={styles.deleteButtonContainer}>
                {deleteButton}
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className={isViewMode ? styles.viewFormCard : styles.editFormCard}>
        {(isEditFormMode || isCreateMode) && (
          <div className={styles.formGroup}>
            <span className={styles.titleName}>Package Name *</span>
            <div className={styles.inputWithClear}>
              <input
                type="text"
                value={formData.custom_combo_name || ''}
                onChange={(e) => {
                  console.log("Input onChange triggered with value:", e.target.value);
                  handleInputChange('custom_combo_name', e.target.value);
                }}
                placeholder="Enter package name"
                className={styles.formInput}
                required
                disabled={!isFormEditable}
              />
            </div>
          </div>
        )}

        {isViewMode && (
          <div className={styles.formGroup}>
            <span className={styles.titleName}>Package Name</span>
            <div className={styles.viewField}>
              {formData.custom_combo_name || "No package name set"}
            </div>
          </div>
        )}

        <div className={styles.formContent}>
          <div className={styles.leftColumn}>
            <div className={styles.itemsSection}>
              <h4 className={styles.sectionTitle}>Included Items</h4>

              {(isEditFormMode || isCreateMode) && (
                <div className={styles.tabsContainer}>
                  {tabs.map(tab => buildIncludedItemsSection(tab))}
                </div>
              )}

              {isViewMode && (
                <div className={styles.cateringCourses}>
                  <div className={styles.coutseItems}>
                    <div className={styles.courseHeader}>Items</div>
                    <div className={styles.courseItemsByList}>
                      {catering_main.length > 0 ? (
                        <>
                          {catering_main.slice(0, 4).map((item, key) => (
                            <div key={key} className={styles.menuItemRow}>
                              <span className={styles.menuItemName}>
                                {item.itemName}
                              </span>
                              <span className={styles.menuItemQuantity}>
                                ({item.qty})
                              </span>
                            </div>
                          ))}
                          {catering_main.length > 4 && (
                            <div style={{ textAlign: "left", width: "100%" }}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSeeMore("Main Items", catering_main);
                                }}
                                className={styles.seeMoreLink}
                              >
                                View More Items ({catering_main.length - 4} more)
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: "left" }}>
                          No items available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Menu Dialog Component */}
                  <MenuDialog
                    isOpen={isDialogOpen}
                    onClose={handleCloseDialog}
                    label={selectedMenu?.label || "Main Items"}
                    mainItems={selectedMenu?.items || []}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {(isEditFormMode || isCreateMode) && (
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={!formValid}
            >
              {isEditFormMode ? 'Save Changes' : 'Create Package'}
            </button>
          </div>
        )}

        {isViewMode && (
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handlePackageSelection}
              className={styles.selectPackageButton}
            >
              Select Package
            </button>
          </div>
        )}
      </form>

      <ItemSelectionModal />
    </div>
  );
});

export default ServiceForm;